<template>
    <div class="p-6">
        <div v-if="searching" class="w-full h-64 bg-grey-light flex justify-center items-center">
            <span><i class="fa fa-circle-notch fa-spin"></i> Loading recently filed. Please wait.</span>
        </div>

        <!-- Table in the Documents filter-bar style: small uppercase headings, name takes the
             spare width, fixed-width columns for the rest so nothing overlaps -->
        <mq-layout mq="tablet+" v-if="!searching">
            <div v-if="files.length > 0">
                <div class="flex flex-row items-center px-3 py-3 border-b text-xxs uppercase tracking-wide text-grey-dark font-semibold">
                    <div class="flex-1 min-w-0">Name</div>
                    <div class="w-32 ml-4 flex-none">Uploaded</div>
                    <div class="w-24 ml-4 flex-none">Type</div>
                    <div class="w-24 ml-4 flex-none">Size</div>
                    <div class="w-64 ml-4 flex-none"></div>
                </div>
                <div class="flex flex-row items-center px-3 py-2 border-b"
                    v-for="(file, index) in files"
                    :key="file.id"
                    :class="{'bg-brand-lightest': index % 2 !== 0}">
                    <div class="flex-1 min-w-0 truncate" :title="file.filename">{{ file.filename }}</div>
                    <div class="w-32 ml-4 flex-none">{{ getDateUploaded(file) }}</div>
                    <div class="w-24 ml-4 flex-none">{{ fileTypeLabel(file) }}</div>
                    <div class="w-24 ml-4 flex-none">{{ fileSizeLabel(file.size) }}</div>
                    <div class="w-64 ml-4 flex-none flex justify-end">
                        <button type="button"
                            class="border border-brand bg-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark"
                            @click.prevent="view(file)">
                            <i class="fa fa-search mr-1"></i> View
                        </button>
                        <button type="button"
                            class="border border-green bg-white text-green text-sm font-medium py-2 px-4 rounded ml-2 hover:bg-green hover:text-white"
                            @click.prevent="archiveFileConfirm(file)">
                            <i class="fa fa-archive mr-1"></i> Archive
                        </button>
                    </div>
                </div>
            </div>
            <div v-else class="w-full h-64 bg-grey-light flex justify-center items-center">
                <span>No documents filed today.</span>
            </div>
        </mq-layout>

        <mq-layout mq="mobile" v-if="!searching">
            <recently-filed-mobile v-if="files.length > 0"
                v-for="(file, index) in files"
                :index="index"
                :file="file"
                :key="file.id"
                @view="view"
                @archive-confirm="archiveFileConfirm">
            </recently-filed-mobile>
            <div v-if="files.length == 0" class="w-full h-64 bg-grey-light flex justify-center items-center">
                <span>No documents filed today.</span>
            </div>
        </mq-layout>

        <p class="text-grey-dark text-sm text-center py-3" v-if="end_of_page">
            End of list
        </p>

        <div v-if="!searching">
            <div class="py-4 flex justify-center" v-if="files.length >= 20 || page > 1">
                <button class="border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded mx-1 hover:bg-grey-lightest"
                    type="button"
                    v-if="next_page_triggered && page > 1"
                    @click.prevent="page--; end_of_page = false; getRecentFiles()">
                    <i class="fa fa-angle-left"></i> Previous
                </button>
                <button class="border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded mx-1 hover:bg-grey-lightest"
                    type="button"
                    v-if="!end_of_page"
                    @click.prevent="page++; next_page_triggered = true; getRecentFiles()">
                    Next <i class="fa fa-angle-right"></i>
                </button>
            </div>
        </div>

        <sweet-modal overlay-theme="dark" ref="archive_confirm">
            <div class="flex flex-col">
                <p class="font-medium text-lg">Archive {{ for_archiving.filename }}?</p>
                <p class="mt-2 text-grey-darker">
                    The document is moved out of this vault's documents and into the archive.
                </p>

                <div class="mt-6 flex justify-center">
                    <button type="button"
                        class="border border-grey bg-white px-6 py-2 text-grey-darker rounded font-medium mr-2 hover:bg-grey-lightest"
                        @click.prevent="$refs.archive_confirm.close()">
                        Cancel
                    </button>
                    <button type="button"
                        class="border border-green bg-green px-6 py-2 text-white rounded font-medium hover:bg-green-dark"
                        @click.prevent="archiveFile">
                        <i class="fa fa-archive mr-1"></i> Archive
                    </button>
                </div>
            </div>
        </sweet-modal>

        <app-modals></app-modals>

    </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppModals from "../../components/AppModals";
import RecentlyFiledMobile from "../../components/RecentlyFiled/RecentlyFiledMobile";
import { fileTypeLabel, fileSizeLabel } from "../../file_labels.mjs";

export default {
  components: {
    AppModals,
    RecentlyFiledMobile
  },

  data() {
    return {
      files: [],

      for_archiving: "",

      page: 1,
      searching: false,

      end_of_page: false,

      next_page_triggered: false
    };
  },

  mounted() {
    this.getRecentFiles();

    this.$ebus.$on("updated-from-fileview", payload => {
      this.getRecentFiles();
    });
  },

  methods: {
    getRecentFiles() {
      this.searching = true;

      let date_range = [];
      date_range["gte"] = moment().format("YYYY/MM/DD");
      date_range["le"] = moment().format("YYYY/MM/DD");

      let search_date = {};
      search_date.gte = date_range["gte"];
      search_date.le = date_range["le"];

      let params = {
        dates: search_date,
        own_uploads_only: 0,
        count: 20,
        page: this.page,
        uploader_id: ""
      };
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/files`, {
          params: params,
          paramsSerializer: function(params) {
            return qs.stringify(params);
          }
        })
        .then(({ data }) => {
          if (data.length == 0 && this.page > 1) {
            this.end_of_page = true;
            this.page--;
          } else {
            this.files = data;
          }

          this.searching = false;
        });
    },

    getDateUploaded(file) {
      let this_date = moment(file.created_at);
      return this_date.format("DD/MM/YYYY");
    },

    fileTypeLabel,
    fileSizeLabel,

    archiveFileConfirm(file) {
      this.for_archiving = "";
      this.for_archiving = file;
      this.$refs.archive_confirm.open();
    },

    archiveFile() {
      axios
        .delete(
          `/api/vaults/${this.for_archiving.vault_id}/files/${
            this.for_archiving.id
          }`
        )
        .then(({ data }) => {
          this.$refs.archive_confirm.close();
          this.$noty.success(`Document has been archived`, {
            killer: true,
            timeout: 2000
          });

          this.searching = true;

          _.delay(() => {
            this.getRecentFiles();
          }, 1500);
        })
        .catch(error => {
          this.$refs.archive_confirm.close();
          this.$noty.error("The document could not be archived", {
            killer: true,
            timeout: 3000
          });
          console.log(error);
        });
    },

    // refileConfirm(file) {
    //   this.for_refile = "";
    //   this.for_refile = file;
    //   this.$refs.refile_confirm.open();
    // },

    // refile() {
    //   let index = _.findIndex(this.files, ["id", this.for_refile.id]);

    //   if (index !== -1) {
    //     this.files.splice(index, 1);
    //   }

    //   axios
    //     .patch(
    //       `/api/vaults/${this.for_refile.vault_id}/files/${
    //         this.for_refile.id
    //       }/mark-as-unfiled`
    //     )
    //     .then(resp => {
    //       this.$refs.refile_confirm.close();
    //       this.$refs.refile_success.open();

    //       this.$ebus.$emit("refresh-vaults");
    //     });

    //   // this.$store.commit('setAsRefiled', file);
    // },

    view(file) {
      axios.get(`api/vaults/${file.vault_id}/files/${file.id}`).then(resp => {
        if (resp.data && resp.data.id) {
          this.$ebus.$emit("view-file", resp.data);
        }
      });
    }
  },

  computed: {
    ...mapGetters({
      user: "getUser"
    })
  }
};
</script>

<style>
</style>
